import { useState } from 'react';
import { Plus, Pencil, Trash2, Power, PowerOff, FlaskConical, Loader2, Bot } from 'lucide-react';
import { useAssistants, useUpdateAssistant, useDeleteAssistant, useTestAssistant } from '@/hooks/useAssistants';
import { AssistantConfigModal } from './AssistantConfigModal';
import type { Assistant } from '@/types/api.types';
import { toast } from 'sonner';
import { asApiError } from '@/utils/error.util';

export const AssistantManagement = () => {
  const { data: assistants, isLoading } = useAssistants();
  const updateMutation = useUpdateAssistant();
  const deleteMutation = useDeleteAssistant();
  const testMutation = useTestAssistant();

  const [editingAssistant, setEditingAssistant] = useState<Assistant | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [testPrompt, setTestPrompt] = useState('');
  const [selectedTestAssistantId, setSelectedTestAssistantId] = useState<string>('');
  const [testingAssistantId, setTestingAssistantId] = useState<string | null>(null);
  const [testResponse, setTestResponse] = useState<string | null>(null);

  const handleToggleEnabled = async (assistant: Assistant) => {
    try {
      await updateMutation.mutateAsync({
        assistantId: assistant.id,
        data: { enabled: !assistant.enabled },
      });
      toast.success(assistant.enabled ? 'Асистентът е деактивиран' : 'Асистентът е активиран');
    } catch (error) {
      toast.error(asApiError(error).message);
    }
  };

  const handleDelete = async (assistant: Assistant) => {
    if (!confirm(`Сигурни ли сте, че искате да изтриете асистент "${assistant.users_assistant_account?.display_name}"?`)) return;
    try {
      await deleteMutation.mutateAsync(assistant.id);
      toast.success('Асистентът е изтрит');
    } catch (error) {
      toast.error(asApiError(error).message);
    }
  };

  const handleTest = async (assistant: Assistant) => {
    if (!testPrompt.trim()) {
      toast.error('Въведете тестово съобщение');
      return;
    }
    setTestingAssistantId(assistant.id);
    setTestResponse(null);
    try {
      const result = await testMutation.mutateAsync({ assistantId: assistant.id, prompt: testPrompt });
      setTestResponse(result.response);
    } catch (error) {
      toast.error(asApiError(error).message);
    } finally {
      setTestingAssistantId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500 dark:text-dark-text-muted">
          Управлявайте AI асистенти, които отговарят когато бъдат споменати с @.
        </p>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
        >
          <Plus className="w-4 h-4" />
          Нов асистент
        </button>
      </div>

      {assistants && assistants.length > 0 ? (
        <div className="bg-white dark:bg-dark-bg-secondary rounded-lg border border-gray-200 dark:border-dark-border-primary overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 dark:bg-dark-bg-hover border-b border-gray-200 dark:border-dark-border-primary">
                <th className="text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider px-4 py-3">Асистент</th>
                <th className="text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider px-4 py-3">Модел</th>
                <th className="text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider px-4 py-3">Обхват</th>
                <th className="text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider px-4 py-3">Статус</th>
                <th className="text-right text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider px-4 py-3">Действия</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-dark-border-primary">
              {assistants.map((assistant) => (
                <tr key={assistant.id} className="hover:bg-gray-50 dark:hover:bg-dark-bg-hover">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                        <Bot className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">
                          @{assistant.slug}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-dark-text-muted">
                          {assistant.description || assistant.users_assistant_account?.display_name}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <span className="font-mono text-xs bg-gray-100 dark:bg-dark-bg-input px-2 py-1 rounded">
                        {assistant.model}
                      </span>
                      {assistant.enable_web_search && (
                        <span className="text-[10px] font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 px-1.5 py-0.5 rounded-full">
                          🌐 Web
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-600 dark:text-dark-text-secondary">
                      {assistant.channel_scope === 'all' ? 'Всички канали' : `${assistant.assistant_channels?.length || 0} канала`}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      assistant.enabled
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-gray-100 text-gray-600 dark:bg-dark-bg-input dark:text-dark-text-muted'
                    }`}>
                      {assistant.enabled ? 'Активен' : 'Неактивен'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => handleToggleEnabled(assistant)}
                        className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-dark-text-primary rounded-md hover:bg-gray-100 dark:hover:bg-dark-bg-input"
                        title={assistant.enabled ? 'Деактивирай' : 'Активирай'}
                      >
                        {assistant.enabled ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => setEditingAssistant(assistant)}
                        className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-dark-text-primary rounded-md hover:bg-gray-100 dark:hover:bg-dark-bg-input"
                        title="Редактирай"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(assistant)}
                        className="p-1.5 text-gray-400 hover:text-red-500 rounded-md hover:bg-gray-100 dark:hover:bg-dark-bg-input"
                        title="Изтрий"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-12 text-gray-400 dark:text-dark-text-muted">
          <Bot className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>Няма създадени асистенти</p>
        </div>
      )}

      {assistants && assistants.length > 0 && (
        <div className="bg-gray-50 dark:bg-dark-bg-hover rounded-lg p-4 space-y-3">
          <h3 className="text-sm font-medium text-gray-700 dark:text-dark-text-secondary">
            <FlaskConical className="w-4 h-4 inline mr-1" />
            Тест на асистент
          </h3>
          <div className="flex gap-2">
            <input
              type="text"
              value={testPrompt}
              onChange={(e) => setTestPrompt(e.target.value)}
              placeholder="Въведете тестово съобщение..."
              className="flex-1 px-3 py-2 border border-gray-300 dark:border-dark-border-primary rounded-lg bg-white dark:bg-dark-bg-input text-sm text-gray-900 dark:text-dark-text-primary"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && selectedTestAssistantId && testPrompt.trim()) {
                  const selected = assistants.find((a) => a.id === selectedTestAssistantId);
                  if (selected) handleTest(selected);
                }
              }}
            />
            <select
              value={selectedTestAssistantId}
              onChange={(e) => setSelectedTestAssistantId(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-dark-border-primary rounded-lg bg-white dark:bg-dark-bg-input text-sm text-gray-900 dark:text-dark-text-primary"
            >
              <option value="">Изберете асистент</option>
              {assistants.map((assistant) => (
                <option key={assistant.id} value={assistant.id}>
                  @{assistant.slug}
                </option>
              ))}
            </select>
            <button
              onClick={() => {
                const selected = assistants.find((a) => a.id === selectedTestAssistantId);
                if (selected) handleTest(selected);
              }}
              disabled={!!testingAssistantId || !testPrompt.trim() || !selectedTestAssistantId}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium flex items-center gap-1"
            >
              {testingAssistantId ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <FlaskConical className="w-3 h-3" />
              )}
              Тест
            </button>
          </div>
          {testResponse && (
            <div className="p-3 bg-white dark:bg-dark-bg-secondary rounded-lg border border-gray-200 dark:border-dark-border-primary text-sm text-gray-700 dark:text-dark-text-secondary whitespace-pre-wrap">
              {testResponse}
            </div>
          )}
        </div>
      )}

      {(showCreateModal || editingAssistant) && (
        <AssistantConfigModal
          assistant={editingAssistant}
          onClose={() => {
            setShowCreateModal(false);
            setEditingAssistant(null);
          }}
        />
      )}
    </div>
  );
};
